import React, { FunctionComponent, useCallback, useMemo } from 'react';

import { useEvent } from 'effector-react/ssr';

import * as Accordion from '@radix-ui/react-accordion';
import clsx from 'clsx';
import { RemoveScroll } from 'react-remove-scroll';

import { CategoriesTree } from '@domains/categories';
import { MenuItem } from '@domains/common-data';
import { SocialContact } from '@domains/contacts';

import { setIsOpenCatalogMenu } from '@stores/header';

import { isValueSet } from '@utils/guards';
import { makeCategoryRoute } from '@utils/route';

import { Container } from '@components/common/Container/Container';
import { SocialContacts } from '@components/common/Header/SocialContacts/SocialContacts';
import { Link } from '@components/common/Link/Link';
import { Picture } from '@components/common/Picture/Picture';
import { StyledScrollArea } from '@components/common/StyledScrollArea/StyledScrollArea';
import { ChevronDownIcon } from '@components/icons';
import { PlaceholderIcon } from '@components/icons/PlaceholderIcon';

import styles from './MobileCatalogWidget.module.scss';

export type CatalogWidgetProps = {
    categories: CategoriesTree;
    isOpen: boolean;
    navigation: MenuItem[];
    contacts: SocialContact[];
    className?: string;
};

export const MobileCatalogWidget: FunctionComponent<CatalogWidgetProps> = (props) => {
    const { categories, className, navigation, contacts, isOpen } = props;

    const setIsOpenCatalogMenuFn = useEvent(setIsOpenCatalogMenu);

    const handleLinkClick = useCallback(() => {
        setIsOpenCatalogMenuFn(false);
    }, [setIsOpenCatalogMenuFn]);

    const hasCategories = categories.length > 0;

    // Prevent closing catalog widgets by useClickOutside
    const preventEventPropagation = (event: React.MouseEvent<HTMLButtonElement>) =>
        event.stopPropagation();

    const categoriesAccordion = useMemo(
        () => (
            <Accordion.Root type="single" collapsible className={styles.accordion}>
                {categories.map(({ thumbnail, slug, name, subcategories }) => (
                    <Accordion.Item key={slug} value={slug} className={styles.accordionItem}>
                        <Accordion.Header asChild>
                            <div className={styles.accordionHeader}>
                                {subcategories.length > 0 ? (
                                    <Accordion.Trigger
                                        onClick={preventEventPropagation}
                                        className={styles.accordionTrigger}
                                    >
                                        <div className={styles.categoryLink}>
                                            {isValueSet(thumbnail) ? (
                                                <Picture
                                                    className={styles.categoryImage}
                                                    classes={{ picture: styles.categoryPicture }}
                                                    sources={thumbnail}
                                                />
                                            ) : (
                                                <div
                                                    className={clsx(
                                                        styles.placeholder,
                                                        styles.categoryPicture,
                                                    )}
                                                >
                                                    <PlaceholderIcon />
                                                </div>
                                            )}
                                            {name}
                                        </div>
                                        <ChevronDownIcon className={styles.chevronIcon} />
                                    </Accordion.Trigger>
                                ) : (
                                    <Link
                                        route={makeCategoryRoute(slug)}
                                        className={clsx(
                                            styles.categoryLink,
                                            styles.hasNoSubcategories,
                                        )}
                                        onClick={preventEventPropagation}
                                    >
                                        {thumbnail ? (
                                            <Picture
                                                className={styles.categoryImage}
                                                classes={{ picture: styles.categoryPicture }}
                                                sources={thumbnail}
                                            />
                                        ) : (
                                            <div
                                                className={clsx(
                                                    styles.placeholder,
                                                    styles.categoryPicture,
                                                )}
                                            >
                                                <PlaceholderIcon />
                                            </div>
                                        )}
                                        {name}
                                    </Link>
                                )}
                            </div>
                        </Accordion.Header>
                        {subcategories.length > 0 && (
                            <Accordion.Content className={styles.accordionContent}>
                                <ul className={styles.subcategoryList}>
                                    <li className={styles.subcategoryListItem}>
                                        <Link
                                            route={makeCategoryRoute(slug)}
                                            className={clsx(
                                                styles.subcategoryLink,
                                                styles.allCategories,
                                            )}
                                            onClick={handleLinkClick}
                                        >
                                            Все товары категории
                                        </Link>
                                    </li>
                                    {subcategories.map(
                                        ({
                                            name: subcategoryTitle,
                                            id: subcategoryId,
                                            slug: subcategorySlug,
                                            thumbnail: subcategoryThumbnail,
                                        }) => (
                                            <li
                                                key={subcategoryId}
                                                className={styles.subcategoryListItem}
                                            >
                                                <Link
                                                    route={makeCategoryRoute(subcategorySlug)}
                                                    className={styles.subcategoryLink}
                                                    onClick={handleLinkClick}
                                                >
                                                    {subcategoryThumbnail !== null && (
                                                        <Picture
                                                            sources={subcategoryThumbnail}
                                                            className={styles.subcategoryImage}
                                                            classes={{
                                                                picture: styles.subcategoryPicture,
                                                            }}
                                                        />
                                                    )}
                                                    {subcategoryTitle}
                                                </Link>
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </Accordion.Content>
                        )}
                    </Accordion.Item>
                ))}
            </Accordion.Root>
        ),
        [categories, handleLinkClick],
    );

    return (
        <RemoveScroll
            className={clsx(styles.root, isOpen && styles.isOpen, className)}
            enabled={isOpen}
            noIsolation
        >
            <StyledScrollArea
                classes={{ root: styles.scrollArea, viewport: styles.scrollAreaViewport }}
            >
                <div className={styles.content}>
                    {categoriesAccordion}
                    {hasCategories && <hr className={styles.separator} />}
                    <Container>
                        <SocialContacts
                            contacts={contacts}
                            classes={{
                                root: styles.contacts,
                                link: styles.contactLink,
                                links: styles.contactLinks,
                            }}
                        />
                        <div className={styles.navigation}>
                            {navigation.map((item, index) => (
                                <Link
                                    key={index}
                                    route={item.url}
                                    onClick={handleLinkClick}
                                    className={styles.navigationLink}
                                    target={item.open_in_new_tab ? '_blank' : '_self'}
                                >
                                    {item.name}
                                </Link>
                            ))}
                        </div>
                    </Container>
                </div>
            </StyledScrollArea>
        </RemoveScroll>
    );
};
